import Script from 'next/script';
import { apolloClientSSR } from 'graphql/apollo-client';
import { CATEGORIES, GET_ROUTE } from 'graphql/queries';
import { useAtom } from 'jotai';
import { currentPageAtom, openFilterAtom, pageSizeAtom } from 'global/store';
import getCategoryId from 'utils/getCategoryId';
import redirectServerToRoot from 'utils/api/redirectServerToRoot';
import revalidateServerSideCache from 'utils/revalidateServerSideCache';
import useCatalogue from 'hooks/products/useCatalogue';
import useBreadcrumbs from 'hooks/products/useCategoryBreadcrumbs';
import useScrollRestoration from 'hooks/UI/useScrollRestoration';
import { Box } from '@xstyled/styled-components';
import SEO from '@/SEO';
import FilterSidebar from '@/Catalogue/Filter/FilterSidebar';
import FilterSlider from '@/Catalogue/Filter/Attributes/Category/CategoryFilterSlider';
import MobileFilter from '@/Catalogue/Filter/MobileFilter';
import Products from '@/Catalogue/Products';
import Sort from '@/Catalogue/Sort';
import Breadcrumbs from '@/UI/Breadcrumbs';
import MobileFilterButton from '@/UI/Button/Filter';
import Layout from '@/UI/Layout';
import { Container } from '@/UI/Layout/Container';
import { SectionTitleSmall } from '@/UI/Primitives/Headings';
import { FlexBox } from '@/UI/Primitives/FlexBox';
import MonochromeButton from '@/UI/Button/Variants/Monochrome';
import Loader from '@/UI/Loader';
import { CatalogueContainer, ResultsContainer } from '@/Catalogue/styles';
import { ZeroProducts } from '@/UI/Icons/Catalogue';
import usePagination from 'hooks/products/usePagination';
import Pagination from '@/Catalogue/Pagination';
import { Markup } from 'interweave';
import parseHTML from 'utils/parseHTML';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import ErrorLayout from '@/UI/Error/ErrorLayout' 
import { Sad404Teddy } from '@/UI/Icons';

export default function Catalogue({
  id,
  categories,
  allCategories,
  name,
  description,
  meta_description,
  meta_title
}) {
  const { t } = useTranslation();

  const { products, loading, pages, count } = useCatalogue();
  const [pageSize, setPageSize] = useAtom(pageSizeAtom);

  const crumbs = useBreadcrumbs();

  const crumbsSchema = crumbs?.map((crumb, i) => {
    return {
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: `baltbaby.com${crumb.path}`
    };
  });
  const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
  const [isMobileFilterOpen] = useAtom(openFilterAtom);

  // Restore scroll position when returning from a product
  useScrollRestoration({ data: products });
  usePagination();
  return (
    <>
      {id ? (
        <Layout categories={allCategories}>
          <SEO
            seo={{
              title: meta_title ? meta_title : `${name} - Baltbaby`,
              description:
                meta_description ??
                `Baltbaby ${name?.toLowerCase()} kategooriast leiate suure valiku kvaliteetseid beebikaupu. Telli ${name?.toLowerCase()} otse koju või tule ise järele.`
            }}
          />
          {crumbsSchema.length > 0 && (
            <Script
              type="application/ld+json"
              id="baltbaby_catalogue_structured_data"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: crumbsSchema
                })
              }}
            />
          )}
          <Container>
            <Breadcrumbs crumbs={crumbs} />

            <CatalogueContainer>
              <FilterSidebar display={{ _: 'none', desktop: 'flex' }} />

              <ResultsContainer>
                <SectionTitleSmall as="h1" pb="2rem">
                  {name}
                </SectionTitleSmall>

                {description && (
                  <Markup
                    content={parseHTML(description)}
                    blockList={(['div'], ['p'])}
                    nowrap
                  />
                )}

                {categories?.length > 0 && (
                  <FlexBox
                    display={{ desktop: 'flex' }}
                    align={{ desktop: 'center' }}
                    spaceX={{ desktop: '1rem' }}
                    mt="2rem"
                  >
                    <h3>{t('category.subcategory')}:</h3>
                    <FilterSlider categories={categories} />
                  </FlexBox>
                )}
                <FlexBox
                  id="product_container"
                  dir={{ _: 'column', mobile: 'row' }}
                  justify={{ mobile: 'space-between', desktop: 'flex-end' }}
                  mt="2rem"
                  whiteSpace="nowrap"
                >
                  <Sort />
                  <MobileFilterButton />
                  {isMobileFilterOpen && <MobileFilter />}
                </FlexBox>

                <Products products={products} />

                {products?.length ? (
                  <>
                    {pages > 0 &&
                      count > 24 &&
                      products &&
                      products?.length !== count && (
                        <FlexBox
                          dir="column"
                          align="center"
                          alignSelf="center"
                          position="relative"
                          mt={42}
                        >
                          {loading ? (
                            <Loader />
                          ) : products.length < 100 ? (
                            <Pagination pages={pages} />
                          ) : (
                            <MonochromeButton
                              py={8}
                              px={{ _: 32, desktop: 56 }}
                              onClick={() => {
                                setCurrentPage(currentPage + 1);
                                setPageSize(pageSize + 24);
                              }}
                            >
                              {t('review.show_more')}
                            </MonochromeButton>
                          )}
                        </FlexBox>
                      )}
                  </>
                ) : (
                  <>
                    {products?.length === 0 && (
                      <FlexBox
                        align="center"
                        maxW={{ _: 300, desktop: 340 }}
                        p="10px 20px"
                        spaceX={20}
                        bg="#F7F7F7"
                        borderRadius={7}
                        fontWeight={700}
                      >
                        <Box as="span" role="img" transform="translateY(8%)">
                          <ZeroProducts />
                        </Box>
                        <span>{t('category.no_products_filter')}</span>
                      </FlexBox>
                    )}
                  </>
                )}
              </ResultsContainer>
            </CatalogueContainer>
          </Container>
        </Layout>
      ) : (
        <Layout categories={allCategories}>
          <ErrorLayout
            statusCode="404"
            title="404: This page could not be found"
            text={t('pages.not_found')}
            image={<Sad404Teddy />}
          />
        </Layout>
      )}
    </>
  );
}

export async function getServerSideProps(context) {
  revalidateServerSideCache(context.res);

  const query = (query, variables) => {
    return apolloClientSSR(context.locale).query({
      query,
      variables,
      context: {
        fetchOptions: { method: 'GET' }
      }
    });
  };

  const queryParams = context.params?.kategooria;
  const urlKey = queryParams?.slice(-1).pop();

  //301 REDIRECTS
  const redirectData = await apolloClientSSR(context.locale).query({
    query: GET_ROUTE,
    variables: { url: `${urlKey}.html` },
    fetchPolicy: 'network-only'
  });

  // CHECK URL REWRITES
  if (redirectData?.data?.route?.relative_url) {
    //CHECK IF OLD URL === NEW URL
    const relativeURL = redirectData?.data?.route?.relative_url.replace(
      '.html',
      ''
    );

    if (relativeURL.split('/').length > 0) {
      let found = false;
      for (const urlPart of relativeURL.split('/')) {
        if (urlPart === urlKey) {
          found = true;
        }
      }
      if (!found) {
        return {
          redirect: {
            permanent: true,
            destination: `/kataloog/${relativeURL}`
          }
        };
      }
    } else if (relativeURL !== urlKey) {

      return {
        redirect: {
          permanent: true,
          destination: `/kataloog/${relativeURL}`
        }
      };
    }
  }

  const id = getCategoryId(urlKey);

  const currentCategoryPromise = query(CATEGORIES, { ids: [id] });
  const allCategoriesPromise = query(CATEGORIES, { ids: '2' });

  const [{ data }, allCategories] = await Promise.all([
    currentCategoryPromise,
    allCategoriesPromise
  ]);

//  if (!data) redirectServerToRoot();

  return {
    props: {
      id: id ? id : null,
      categories: data?.categoryList[0]?.children ?? null,
      name: data?.categoryList[0]?.name ?? null,
      meta_description: data?.categoryList[0]?.meta_description ?? null,
      meta_title: data?.categoryList[0]?.meta_title ?? null,
      description: data?.categoryList[0]?.description ?? null,
      allCategories: allCategories.data.categoryList
        ? allCategories.data.categoryList[0].children
        : [],
      ...(await serverSideTranslations(context.locale))
    }
  };
}
